// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Slot Cabinet
// Wrapper component for all slot machine games (like ArcadeCabinet)
// ================================================================
import React from 'react';

export default function SlotCabinet({ title, icon, children, stats, theme = 'forge' }) {
  const colors = THEMES[theme] || THEMES.forge;

  return (
    <div style={styles.cabinet}>
      {/* Marquee header */}
      <div style={{ ...styles.marquee, background: colors.marqueeGradient, borderColor: colors.accent }}>
        <div style={styles.marqueeInner}>
          <span style={styles.marqueeIcon}>{icon}</span>
          <span style={{ ...styles.marqueeTitle, color: colors.accent, textShadow: `0 0 10px ${colors.accent}, 0 0 20px ${colors.accent}, 0 0 40px ${colors.accent}` }}>
            {title}
          </span>
        </div>
        <div style={styles.marqueeLights}>
          {[...Array(16)].map((_, i) => (
            <span key={i} style={{
              ...styles.light,
              animationDelay: `${i * 0.12}s`,
              background: i % 4 === 0 ? colors.accent : i % 4 === 1 ? '#FFD700' : i % 4 === 2 ? '#00FF88' : '#FF00FF',
            }} />
          ))}
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div style={{ ...styles.statsBar, borderColor: colors.accent }}>
          {stats.map((s, i) => (
            <div key={i} style={styles.statItem}>
              <span style={{ ...styles.statLabel, color: `${colors.accent}88` }}>{s.label}</span>
              <span style={{ ...styles.statValue, color: colors.statColor, textShadow: `0 0 8px ${colors.statColor}` }}>{s.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Screen area */}
      <div style={{ ...styles.screenBezel, borderColor: colors.accent }}>
        <div style={{ ...styles.screen, boxShadow: `inset 0 0 60px ${colors.accent}08, 0 0 2px ${colors.accent}44` }}>
          <div style={styles.scanlines} />
          <div style={styles.screenContent}>
            {children}
          </div>
        </div>
      </div>

      {/* Bottom trim */}
      <div style={{ ...styles.bottomTrim, borderColor: colors.accent }}>
        <span style={{ ...styles.forgeText, color: colors.accent }}>$FORGE CASINO</span>
        <span style={styles.provablyFair}>PROVABLY FAIR</span>
      </div>

      <style>{`
        @keyframes slotBlink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes slotPulse { 0%,100%{text-shadow:0 0 10px ${colors.accent},0 0 20px ${colors.accent}} 50%{text-shadow:0 0 5px ${colors.accent},0 0 10px ${colors.accent}} }
        @keyframes scanMove { 0%{background-position:0 0} 100%{background-position:0 4px} }
      `}</style>
    </div>
  );
}

const THEMES = {
  forge: {
    accent: '#DC2626',
    marqueeGradient: 'linear-gradient(180deg, #1a0000 0%, #0a0000 100%)',
    statColor: '#FFD700',
  },
  gold: {
    accent: '#FFD700',
    marqueeGradient: 'linear-gradient(180deg, #1a1500 0%, #0a0800 100%)',
    statColor: '#00FF88',
  },
  neon: {
    accent: '#00FF88',
    marqueeGradient: 'linear-gradient(180deg, #001a0a 0%, #000a05 100%)',
    statColor: '#FFD700',
  },
  royal: {
    accent: '#7C3AED',
    marqueeGradient: 'linear-gradient(180deg, #0a001a 0%, #050010 100%)',
    statColor: '#FFD700',
  },
};

const styles = {
  cabinet: {
    width: '100%',
    maxWidth: 560,
    margin: '0 auto',
    fontFamily: "'Courier New', monospace",
  },
  marquee: {
    border: '3px solid',
    borderBottom: 'none',
    borderRadius: '16px 16px 0 0',
    padding: '16px 16px 10px',
    textAlign: 'center',
    position: 'relative',
  },
  marqueeInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  marqueeIcon: { fontSize: 32 },
  marqueeTitle: {
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: 3,
    textTransform: 'uppercase',
    animation: 'slotPulse 2s ease-in-out infinite',
  },
  marqueeLights: {
    display: 'flex',
    justifyContent: 'center',
    gap: 5,
    marginTop: 10,
  },
  light: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    animation: 'slotBlink 1s ease-in-out infinite',
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'space-around',
    background: '#0a0a0a',
    border: '3px solid',
    borderTop: '1px solid #333',
    borderBottom: 'none',
    padding: '10px 12px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
  },
  statLabel: {
    fontSize: 9,
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontWeight: 700,
  },
  statValue: {
    fontSize: 14,
    fontWeight: 900,
  },
  screenBezel: {
    background: '#0a0a0a',
    border: '3px solid',
    borderTop: '1px solid #333',
    borderBottom: 'none',
    padding: '12px',
  },
  screen: {
    position: 'relative',
    background: '#000',
    borderRadius: 8,
    overflow: 'hidden',
    border: '2px solid #222',
  },
  scanlines: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
    animation: 'scanMove 0.5s linear infinite',
    pointerEvents: 'none',
    zIndex: 10,
  },
  screenContent: {
    position: 'relative',
    minHeight: 320,
  },
  bottomTrim: {
    background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)',
    border: '3px solid',
    borderTop: '1px solid #333',
    borderRadius: '0 0 16px 16px',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  forgeText: {
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 2,
  },
  provablyFair: {
    fontSize: 8,
    color: '#00FF88',
    fontWeight: 700,
    letterSpacing: 1,
    textShadow: '0 0 6px #00FF88',
    animation: 'slotBlink 2s ease-in-out infinite',
  },
};
